/**
 * Generic event bus for Mirrably widgets.
 *
 * All events are dispatched as native CustomEvents on the host element so
 * consumers can listen with addEventListener('<prefix>:open', ...).
 *
 * The event name prefix is configurable per widget instance -- eyewear uses
 * 'forge-tryon', jewelry uses 'forge-jewelry', makeup uses 'forge-makeup'.
 */

/** Union of all standard event names emitted by Mirrably widgets. */
export type MirrablyCoreEventName =
  | 'open'
  | 'close'
  | 'product-change'
  | 'variant-change'
  | 'snapshot'
  | 'error'
  | 'tracking-start'
  | 'tracking-lost'
  | 'add-to-cart'
  | 'cart-success'
  | 'cart-error'
  | 'face-shape'
  | 'pd-measured';

/** Shape of every CustomEvent detail payload emitted by the widget. */
export type MirrablyEvent<T = unknown> =
  | { type: MirrablyCoreEventName; timestamp: number; payload: T }
  | { type: MirrablyCoreEventName; timestamp: number; payload?: undefined };

/** Generic callback type for event listeners. */
export type MirrablyListener<T = unknown> = (event: MirrablyEvent<T>) => void;

/**
 * Thin event bus attached to a single host element.
 *
 * Events are dispatched as bubbling, composed CustomEvents using the naming
 * convention `<prefix>:<eventName>` so they cross Shadow DOM boundaries.
 */
export class EventBus {
  private readonly host: HTMLElement;
  private readonly prefix: string;
  private readonly listeners = new Map<
    MirrablyCoreEventName,
    Set<MirrablyListener<unknown>>
  >();

  /**
   * @param host   - The host element to dispatch DOM events on.
   * @param prefix - Event name prefix for DOM events (e.g. 'forge-tryon').
   *                 Defaults to 'mirrably'.
   */
  constructor(host: HTMLElement, prefix = 'mirrably') {
    this.host = host;
    this.prefix = prefix;
  }

  /**
   * Dispatch a custom event on the host element.
   *
   * @param type   - One of the recognised event names.
   * @param detail - Optional payload merged into the MirrablyEvent detail.
   */
  emit<T = unknown>(type: MirrablyCoreEventName, detail?: T): void {
    const eventDetail = (
      detail !== undefined
        ? { type, timestamp: Date.now(), payload: detail }
        : { type, timestamp: Date.now() }
    ) as MirrablyEvent<T>;

    // Notify internal subscribers
    const bucket = this.listeners.get(type);
    if (bucket) {
      for (const cb of bucket) {
        try {
          (cb as MirrablyListener<T>)(eventDetail);
        } catch (err) {
          console.error(`[${this.prefix}] listener error for "${type}":`, err);
        }
      }
    }

    // Dispatch DOM event so host-page listeners can react
    this.host.dispatchEvent(
      new CustomEvent<MirrablyEvent<T>>(`${this.prefix}:${type}`, {
        detail: eventDetail,
        bubbles: true,
        composed: true, // Crosses Shadow DOM boundary
      }),
    );
  }

  /**
   * Subscribe to an internal event.
   *
   * @param type     - Event name to listen for.
   * @param callback - Function to call when the event fires.
   */
  on<T = unknown>(type: MirrablyCoreEventName, callback: MirrablyListener<T>): void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(callback as MirrablyListener<unknown>);
  }

  /**
   * Remove a previously registered listener.
   *
   * @param type     - Event name.
   * @param callback - The exact function reference passed to `on()`.
   */
  off<T = unknown>(type: MirrablyCoreEventName, callback: MirrablyListener<T>): void {
    this.listeners.get(type)?.delete(callback as MirrablyListener<unknown>);
  }

  /** Remove all internal listeners (does not affect DOM listeners on the host). */
  destroy(): void {
    this.listeners.clear();
  }
}
